import React, { useEffect, useState } from "react";
import { Table, Drawer, Button, Spin, message, Modal } from "antd";
import { axiosInstant } from 'src/api/api';
import { useUser } from 'src/context';
import { useNavigate } from "react-router-dom";

interface Invitation {
  token: string;
  jobTitle: string;
  jobDescription: string;
  salary: string;
  recruiterName: string;
  status: string;
  preScreeningQuestions?: string[];
  companyName: string;
  positionTitle: string;
  workType: string;
  location: string;
  predefinedQuestions?: string[];
  customQuestions?: string[];
  feedback?: string;
}

function renderJobDescription(text: string | undefined) {
  if (!text) return <span>No job description</span>;
  const lines = text.split(/\n/);
  const elements: JSX.Element[] = [];
  let bullets: string[] = [];

  lines.forEach((line: string, idx: number) => {
    const trimmed = line.trim();
    if (/^[-•*]\s+/.test(trimmed)) {
      bullets.push(trimmed.replace(/^[-•*]\s+/, ""));
    } else {
      if (bullets.length) {
        elements.push(
          <ul key={`ul-${idx}`}>
            {bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        );
        bullets = [];
      }
      if (trimmed) {
        elements.push(<p key={`p-${idx}`}>{trimmed}</p>);
      }
    }
  });
  if (bullets.length) {
    elements.push(
      <ul key={`ul-last`}>
        {bullets.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    );
  }
  return elements;
}

const InvitationsPage = () => {
  const { user } = useUser();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvite, setSelectedInvite] = useState<Invitation | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user?.email) return;
    axiosInstant.get(`/invites?candidateEmail=${user.email}`)
      .then(res => {
        setInvitations(res.data);
        setLoading(false);
      });
  }, [user]);

  const openDrawer = (invite: Invitation) => {
    setSelectedInvite(invite);
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setSelectedInvite(null);
  };

  const handleAccept = async () => {
    if (!selectedInvite) return;

    // Fetch the candidate's profile
    const profileRes = await axiosInstant.get(`/profile?userId=${user._id}`);
    const profile = profileRes.data;

    // Define required fields for completeness
    const requiredFields = [
      'firstName', 'lastName', 'email', 'phoneNumber', 'resume', 'profilePhoto'
      // add more as needed
    ];
    const missing = requiredFields.filter(field => !profile[field]);

    if (missing.length > 0) {
      message.error("Please complete your profile before accepting the invitation.");
      // Optionally, open the profile setup page/modal here
      return;
    }

    // Proceed to accept the invite
    const res = await axiosInstant.post(`/invites/accept/${selectedInvite.token}`, {
      candidateId: user._id,
    });
    message.success("Invitation accepted! Redirecting to your dashboard...");
    setDrawerVisible(false);
    navigate('/candidate/dashboard');
  };

  const handleDecline = async () => {
    if (!selectedInvite) return;
    await axiosInstant.post(`/invites/decline/${selectedInvite.token}`, {
      candidateId: user._id,
    });
    message.info("Invitation declined.");
    setDrawerVisible(false);
    // Refresh invitations list
    axiosInstant.get(`/invites?candidateEmail=${user.email}`).then(res => {
      setInvitations(res.data);
    });
  };

  const columns = [
    { 
      title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Company</span>, 
      dataIndex: "companyName", 
      key: "companyName",
      align: 'center' as const,
      render: (text: string) => <span style={{ fontSize: '16px' }}>{text}</span>
    },
    { 
      title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Position</span>, 
      dataIndex: "positionTitle", 
      key: "positionTitle",
      align: 'center' as const,
      render: (text: string) => <span style={{ fontSize: '16px' }}>{text}</span>
    },
    { 
      title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Recruiter</span>, 
      dataIndex: "recruiterName", 
      key: "recruiterName",
      align: 'center' as const,
      render: (text: string) => <span style={{ fontSize: '16px' }}>{text}</span>
    },
    { 
      title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Status</span>, 
      dataIndex: "status", 
      key: "status",
      align: 'center' as const,
      render: (text: string) => <span style={{ fontSize: '16px' }}>{text}</span>
    },
    {
      title: <span style={{ fontSize: '18px', fontWeight: 600 }}>Details</span>,
      key: "action",
      align: 'center' as const,
      render: (_: any, record: Invitation) => (
        <Button type="link" style={{ fontSize: '16px', height: '36px' }} onClick={() => openDrawer(record)}>
          View Details
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ fontSize: '36px', fontWeight: 'bold', color: '#2f54eb' }}>Invitations</h2>
      {loading ? (
        <Spin />
      ) : (
        <Table
          dataSource={invitations}
          columns={columns}
          rowKey="token"
          pagination={false}
        />
      )}
      <Drawer
        title={<span style={{ fontSize: '20px', fontWeight: 'bold' }}>{selectedInvite?.positionTitle}</span>}
        placement="right"
        width={400}
        onClose={closeDrawer}
        visible={drawerVisible}
      >
        {selectedInvite && (
          <>
            <div style={{ fontSize: '16px' }}>
              <p><b>Location:</b> {selectedInvite.location}</p>
              <p><b>Work Type:</b> {selectedInvite.workType}</p>
              <p><b>Salary:</b> {selectedInvite.salary}</p>
              <b>Job Description:</b>
              <div>
                {renderJobDescription(selectedInvite.jobDescription)}
              </div>
              {selectedInvite.predefinedQuestions && selectedInvite.predefinedQuestions.length > 0 && (
                <>
                  <b>Pre-screening Questions:</b>
                  <ul>
                    {selectedInvite.predefinedQuestions.map((q, idx) => <li key={idx}>{q}</li>)}
                  </ul>
                </>
              )}
              {selectedInvite.customQuestions && selectedInvite.customQuestions.length > 0 && (
                <>
                  <b>Custom Questions:</b>
                  <ul>
                    {selectedInvite.customQuestions.map((q, idx) => <li key={idx}>{q}</li>)}
                  </ul>
                </>
              )}
            </div>
            <div style={{ marginTop: 24 }}>
              <Button type="primary" onClick={handleAccept} style={{ marginRight: 8, fontSize: '16px' }}>
                Accept
              </Button>
              <Button danger onClick={handleDecline} style={{ fontSize: '16px' }}>
                Decline
              </Button>
            </div>
          </>
        )}
      </Drawer>
    </div>
  );
};

export default InvitationsPage;
